import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CardCoverageStatus,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import {
  getCardCoverageDescription,
  messages,
} from '@sorare/core/src/lib/cardCoverage';
import { fantasy, playerUnavailability } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { TooltipContent } from '@sorare/marketplace/src/components/card/TooltipContent';

import type {
  BenchExtraTooltip_anyCardInterface,
  BenchExtraTooltip_anyPlayerInterface,
  BenchExtraTooltip_so5Lineup,
  BenchExtraTooltip_suspension,
} from './__generated__/index.graphql';

type Props = {
  children: ReactNode;
  anyCard: BenchExtraTooltip_anyCardInterface | undefined;
  anyPlayer: BenchExtraTooltip_anyPlayerInterface;
  suspensions: BenchExtraTooltip_suspension[];
  so5LineupId?: string;
  showInjury?: boolean;
  showSuspension?: boolean;
};

export const BenchExtraTooltip = ({
  children,
  anyCard,
  anyPlayer,
  suspensions,
  so5LineupId,
  showInjury = true,
  showSuspension = true,
}: Props): ReactNode => {
  const usedInLineups = isType(anyCard?.usedIn, 'UsedInSo5Lineups')
    ? anyCard?.usedIn?.so5Lineups
    : undefined;

  const lineupUsingCard = usedInLineups?.find(
    so5Lineup => so5Lineup && so5Lineup.id !== so5LineupId
  );

  const listed = (anyCard?.sentInLiveOffers ?? []).length > 0;

  const [injury] = anyPlayer.activeInjuries;
  const [suspension] = suspensions;

  if (anyCard && lineupUsingCard?.so5Leaderboard) {
    const { so5Leaderboard } = lineupUsingCard;
    const { so5LeaderboardGroup } = so5Leaderboard;

    const so5League = isType(so5LeaderboardGroup, 'So5League')
      ? so5LeaderboardGroup
      : null;
    const so5LeaderboardGroupName = isType(so5LeaderboardGroup, 'So5League')
      ? so5League?.shortDisplayName
      : so5LeaderboardGroup?.displayName;

    const name = isType(anyCard, 'Card')
      ? so5LeaderboardGroupName
      : so5Leaderboard.displayName;
    const rarity =
      so5Leaderboard.mainRarityType && so5Leaderboard.mainRarityType in Rarity
        ? Rarity[so5Leaderboard.mainRarityType as Rarity]
        : 'mix';
    const teamName = lineupUsingCard.managerTeam?.name || lineupUsingCard.name;

    return (
      <Tooltip
        title={
          <Vertical gap={0.5}>
            <Horizontal gap={0.5}>
              <LabelS>
                <FormattedMessage
                  id="BenchExtraTooltip.playingIn"
                  defaultMessage="Playing in {name}"
                  values={{
                    name,
                  }}
                />
              </LabelS>
            </Horizontal>
            <LabelS color="var(--c-nd-600)">
              {teamName}
              {' - '}
              <FormattedMessage {...scarcityMessages[rarity]} />
              {!!so5Leaderboard.division && (
                <>
                  {' '}
                  <FormattedMessage
                    {...fantasy.division}
                    values={{ division: so5Leaderboard.division }}
                  />
                </>
              )}
            </LabelS>
          </Vertical>
        }
      >
        {children}
      </Tooltip>
    );
  }
  if (
    isType(anyCard, 'Card') &&
    anyCard.coverageStatus !== CardCoverageStatus.FULL
  ) {
    return (
      <Tooltip
        title={
          <TooltipContent
            title={messages[anyCard.coverageStatus]}
            description={getCardCoverageDescription(anyCard.coverageStatus)}
          />
        }
      >
        {children}
      </Tooltip>
    );
  }
  if (injury && showInjury) {
    return (
      <Tooltip
        title={
          <Vertical gap={0.5}>
            <Horizontal gap={0.5}>
              <LabelS>{injury.kind}</LabelS>
              {!!injury.startDate && (
                <LabelS color="var(--c-nd-600)">
                  <FormattedMessage
                    {...playerUnavailability.unavailableSince}
                    values={{ unavailableSince: injury.startDate }}
                  />
                </LabelS>
              )}
            </Horizontal>
            <LabelS color="var(--c-nd-600)">
              {injury.expectedEndDate ? (
                <FormattedMessage
                  {...playerUnavailability.expectedReturnDate}
                  values={{ date: injury.expectedEndDate }}
                />
              ) : (
                <FormattedMessage {...playerUnavailability.unknownReturnDate} />
              )}
            </LabelS>
          </Vertical>
        }
      >
        {children}
      </Tooltip>
    );
  }
  if (suspension && showSuspension) {
    return (
      <Tooltip
        title={
          <Vertical gap={0.5}>
            <Horizontal gap={0.5}>
              <LabelS>{suspension.reason}</LabelS>
              {!!suspension.matches && (
                <>
                  {' '}
                  <LabelS color="var(--c-nd-600)">
                    <FormattedMessage
                      {...playerUnavailability.gamesUnavailableCount}
                      values={{
                        gamesCount: suspension.matches,
                        competition: suspension.competition.displayName,
                      }}
                    />
                  </LabelS>
                </>
              )}
            </Horizontal>
            <LabelS color="var(--c-nd-600)">
              {suspension.endDate ? (
                <FormattedMessage
                  {...playerUnavailability.expectedReturnDate}
                  values={{ date: suspension.endDate }}
                />
              ) : (
                <FormattedMessage {...playerUnavailability.unknownReturnDate} />
              )}
            </LabelS>
          </Vertical>
        }
      >
        {children}
      </Tooltip>
    );
  }
  if (listed) {
    return (
      <Tooltip
        title={
          <Vertical gap={0.5}>
            <Horizontal gap={0.5}>
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage {...fantasy.removeCard} />
              </LabelS>
            </Horizontal>
          </Vertical>
        }
      >
        {children}
      </Tooltip>
    );
  }

  return children;
};

const so5LineupFragment = gql`
  fragment BenchExtraTooltip_so5Lineup on So5Lineup {
    id
    name
    managerTeam {
      id
      name
    }
    so5Leaderboard {
      slug
      division
      displayName
      mainRarityType
      so5LeaderboardGroup {
        slug
        displayName
        ... on So5League {
          slug
          shortDisplayName
        }
      }
    }
  }
` as TypedDocumentNode<BenchExtraTooltip_so5Lineup>;

BenchExtraTooltip.fragments = {
  so5Lineup: so5LineupFragment,
  anyCard: gql`
    fragment BenchExtraTooltip_anyCardInterface on AnyCardInterface {
      slug
      sentInLiveOffers {
        id
      }
      usedIn(
        so5LeaderboardSlug: $so5LeaderboardSlug
        taskId: $taskId
        gameId: $gameId
      ) {
        slug
        ... on UsedInSo5Lineups {
          slug
          so5Lineups {
            id
            ...BenchExtraTooltip_so5Lineup
          }
        }
      }
      ... on Card {
        slug
        coverageStatus
      }
    }
  ` as TypedDocumentNode<BenchExtraTooltip_anyCardInterface>,
  anyPlayer: gql`
    fragment BenchExtraTooltip_anyPlayerInterface on AnyPlayerInterface {
      slug
      activeInjuries {
        id
        startDate
        expectedEndDate
        kind
      }
    }
  ` as TypedDocumentNode<BenchExtraTooltip_anyPlayerInterface>,
  suspension: gql`
    fragment BenchExtraTooltip_suspension on Suspension {
      id
      startDate
      endDate
      reason
      matches
      competition {
        slug
        id
        displayName
      }
    }
  ` as TypedDocumentNode<BenchExtraTooltip_suspension>,
};
